import type { SerializedNodes } from '@craftjs/core';

import type { EducationCategory } from '@/modules/education/Education.constants';

/**
 * Current schema version for CommonData.
 * Update this when making changes to the CommonData structure.
 *
 * Versioning guidelines:
 * - Bump major for breaking changes (removing props, changing types)
 * - Bump minor for additive changes (new block types, new optional props)
 * - Bump patch for bug fixes to serialization
 *
 * Version history:
 * - 1.0.0: Initial version
 * - 1.1.0: Added iconColor token to PresentationListItem (previously undefined)
 * - 1.2.0: Removed SECONDARY icon color, migrated to PRIMARY
 * - 1.3.0: Converted EditableCallout from text prop to canvas with children
 */
export const CURRENT_SCHEMA_VERSION = '1.3.0';

/**
 * Core block types provided by the block editor.
 *
 * Layout blocks:
 * - TwoColumnLayout: Two-column layout with named slots (leftColumn, rightColumn)
 *
 * Container blocks (used in CommonData, mapped to BlockEditorCanvas in Craft.js):
 * - ListItemsSection: Vertical list of content blocks
 *
 * Consumers can extend this with their own block types via the resolver.
 */
export type CoreBlockType = 'ListItemsSection' | 'TwoColumnLayout';

/**
 * Block type is a string to allow for extensibility.
 * Core types are defined above, but consumers can add their own.
 */
export type BlockType = CoreBlockType | string;

/**
 * Display names for block types.
 * Used for Craft.js `allowedTypes` rules and UI display.
 * Note: ListItemsSection is a CommonData concept that maps to BlockEditorCanvas in Craft.js.
 */
export const BLOCK_DISPLAY_NAMES = {
  // Layout blocks
  ListItemsSection: 'List Items Section',
  TwoColumnLayout: 'Two Column Layout',
  // Content blocks
  PresentationListItem: 'Presentation List Item',
  EducationalSlideHeading: 'Educational Slide Section Heading',
  EditableEducationalSlideBody: 'Educational Slide Body Text',
  EditableCallout: 'Callout',
} as const;

/**
 * Common allowed type combinations for canvas containers.
 * Use these instead of inline string arrays to avoid typos and inconsistencies.
 * Consumers can extend these or create their own allowed type arrays.
 */
export const ALLOWED_TYPES = {
  containerContent: [
    BLOCK_DISPLAY_NAMES.PresentationListItem,
    BLOCK_DISPLAY_NAMES.EducationalSlideHeading,
    BLOCK_DISPLAY_NAMES.EditableEducationalSlideBody,
    BLOCK_DISPLAY_NAMES.EditableCallout,
  ],
  /** Content allowed inside a Callout (heading, body text, list items - no nested callouts) */
  calloutContent: [
    BLOCK_DISPLAY_NAMES.PresentationListItem,
    BLOCK_DISPLAY_NAMES.EducationalSlideHeading,
    BLOCK_DISPLAY_NAMES.EditableEducationalSlideBody,
  ],
  /** Presentation list item content only (no headings) */
  listItemsOnly: [BLOCK_DISPLAY_NAMES.PresentationListItem],
  /** Top-level blocks that can be added to the root canvas */
  layoutContainers: [
    BLOCK_DISPLAY_NAMES.TwoColumnLayout,
    BLOCK_DISPLAY_NAMES.ListItemsSection,
  ],
} as const;

/**
 * A block in the CommonData format.
 * This is our canonical, framework-agnostic representation of content.
 */
export interface CommonBlock {
  id: string;
  type: BlockType;
  props: Record<string, unknown>;
  /**
   * Children can be:
   * - Simple children: Use 'default' key (e.g., for ListItemsSection)
   * - Named slots: Use slot names like 'leftColumn', 'rightColumn' (for TwoColumnLayout)
   */
  children?: Record<string, CommonBlock[]>;
}

/**
 * The root data structure for block editor content.
 * This format is framework-agnostic and is what gets persisted to the backend.
 */
export interface CommonData {
  /**
   * Schema version for future migrations.
   * Should match CURRENT_SCHEMA_VERSION when created.
   */
  version: string;
  /** Layout preferences for the built-in Education Page, stored with its tenant override. */
  educationSlideOptions?: {
    /** Omit the chart, legend, and assumptions on Why estate planning matters. */
    hideWealthDistributionGraph?: boolean;
  };
  /**
   * Category for custom education slides. Used to organize slides in the UI.
   */
  educationCategory?: EducationCategory;
  blocks: CommonBlock[];
}

export type EditableBlocks = SerializedNodes;
